// Invoice documents and statuses (Stage 2E-3): display status (stored draft/issued/cancelled + sent date; payment state and
// overdue DERIVED by the database view), the issued copy (from the frozen snapshots, the database's lines/totals and the
// numbers of linked documents), stored once with its SHA-256, the verified download, the CANCELLED-marked copy and the
// draft preview. Never calculates money and never touches Accounts.
import { invoicePdfBytes } from "./invoicePdf.js";
import { loadInvoice, loadInvoiceLines, loadQuotation, loadPdfImage, loadStampImage, storeInvoicePdf, openStoredPdf } from "./api";
import { pdfFileName } from "./format.js";

// D6 (before the 2E-4 payment screens): Draft · Issued · Sent · Partially paid · Paid · Cancelled, plus an Overdue flag.
export function invStatus(v) {
    if (v.status === "draft") return "draft";
    if (v.status === "cancelled") return "cancelled";
    if (v.payment_status === "paid") return "paid";
    if (v.payment_status === "partially_paid") return "partially_paid";
    return v.sent_at ? "sent" : "issued";
}
export const INV_STATUS = {
    draft: ["Draft", "draft"], issued: ["Issued", "current"], sent: ["Sent", "upcoming"], partially_paid: ["Partially paid", "partpaid"],
    paid: ["Paid", "paid"], cancelled: ["Cancelled", "overdue"],
};
export const INV_OPEN = ["draft", "issued", "sent", "partially_paid"];
export const isOverdue = (v) => v.status === "issued" && !!v.overdue;

async function images(issuer) {
    const [logo, qr] = await Promise.all([loadPdfImage(issuer.logo_path), loadPdfImage(issuer.qr_path)]);
    return { logo, qr };
}
// Numbers of the documents an invoice is linked to (both are fixed once the invoice is issued).
async function links(v) {
    const [q, r] = await Promise.all([v.quotation_id ? loadQuotation(v.quotation_id) : null, v.replaces_invoice_id ? loadInvoice(v.replaces_invoice_id) : null]);
    return { quotationNo: q?.doc_no || null, replacesNo: r?.doc_no || null };
}

// The issued copy: re-read the invoice and its lines from the database and draw them from the snapshots.
export async function issuedInvoiceBytes(id, cal, mode = "issued", replacedByNo = null) {
    const [v, lines] = await Promise.all([loadInvoice(id), loadInvoiceLines(id)]);
    if (!v || v.status === "draft" || !v.issuer_snapshot) throw new Error("Only an issued invoice has an issued copy.");
    const [{ logo, qr }, l, stamp] = await Promise.all([images(v.issuer_snapshot), links(v), loadStampImage()]);
    return { v, bytes: invoicePdfBytes({ mode, invoice: v, lines, issuer: v.issuer_snapshot, client: v.client_snapshot, cal, ...l, replacedByNo: mode === "cancelled" ? replacedByNo : null, logo, qr, stamp }) };
}
// "Create stored copy" — at issue, and later as the recovery when storing failed. Never replaces an existing copy.
export async function createStoredInvoiceCopy(id, cal) {
    const { v, bytes } = await issuedInvoiceBytes(id, cal, "issued");
    if (v.issued_pdf_path) return v;
    return storeInvoicePdf(v, bytes);
}
export const cancelledInvoiceBytes = async (id, cal, replacedByNo) => (await issuedInvoiceBytes(id, cal, "cancelled", replacedByNo)).bytes;
export const storedInvoiceBytes = (v) => openStoredPdf(v);

// Draft preview from the current settings/client and the database's preview lines and totals.
export async function draftInvoiceBytes({ draft, preview, settings, client, cal, quotationNo, replacesNo }) {
    const { logo, qr } = await images(settings || {});
    const v = { ...draft, ...preview, doc_no: null, issued_at: null, updated_at: new Date().toISOString() };
    return invoicePdfBytes({ mode: "draft", invoice: v, lines: preview.lines, issuer: settings || {}, client: client || {}, cal, quotationNo, replacesNo, logo, qr });
}
export const invoiceFileName = (v, suffix) => pdfFileName(v.doc_no, suffix);
